import type React from 'react';
import type { ReactNode, HTMLAttributes } from 'react';

export const AI_ASSISTANT_CONVERSATIONS_KEY = 'ai-assistant-conversations';
export const AI_ASSISTANT_COHORTS_KEY = 'ai-assistant-cohorts';
export const INPUT_MIN_HEIGHT = 46;
export const INPUT_MAX_HEIGHT = 200;
export const INLINE_CONFIRMATION_MS = 2000;
export const INLINE_CONFIRMATION_FADE_MS = 400;
export const INLINE_CONFIRMATION_TOTAL_MS =
  INLINE_CONFIRMATION_MS + INLINE_CONFIRMATION_FADE_MS;
export const COPY_CONFIRMATION_LABEL = 'Copied';
export const MESSAGE_ACTION_BUTTON_CLASS = '!size-8 !p-2 !text-gray-500';
export const AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_PX = 700;
export const AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS = 'max-w-[700px]';

//Used to check the BE error type. Confirms whether an error is due to hitting the token limit
export const USAGE_LIMIT_REACHED_ERROR_TYPE =
  'https://www.marketviewedu.com/error/usage-limit-reached';

interface MarkdownTableProps extends HTMLAttributes<HTMLTableElement> {
  children?: ReactNode;
  className?: string;
}

export const AI_ASSISTANT_TABLE_CLASSES = {
  wrapper: 'overflow-x-auto my-3',
  table: 'w-full min-w-0 border border-gray-200 border-collapse',
  th: 'border border-gray-200 px-3 py-2 text-left font-semibold bg-gray-50',
  td: 'border border-gray-200 px-3 py-2 text-left font-normal',
} as const;

export const MARKDOWN_OVERRIDES = {
  ul: {
    props: {
      className: 'list-disc list-inside pl-4 my-2 space-y-1',
    },
  },
  ol: {
    props: {
      className: 'list-decimal list-inside pl-4 my-2 space-y-1',
    },
  },
  a: {
    props: {
      className: 'underline text-blue-700',
    },
  },
  hr: {
    props: {
      className: 'my-3 border-gray-200',
    },
  },
  table: {
    component: ({
      children,
      className,
      ...rest
    }: MarkdownTableProps): React.JSX.Element => (
      <div className={AI_ASSISTANT_TABLE_CLASSES.wrapper}>
        <table className={className} {...rest}>
          {children}
        </table>
      </div>
    ),
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.table,
    },
  },
  th: {
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.th,
    },
  },
  td: {
    props: {
      className: AI_ASSISTANT_TABLE_CLASSES.td,
    },
  },
};

export const FEEDBACK_TYPES = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
};

export const AI_ASSISTANT_ROUTE = '/ai-assistant';
export const TERMS_URL = 'https://marketviewedu.com/terms-conditions/';
export const GUIDE_FAQ_URL =
  'https://app.marketviewedu.com/learn/content/guides/18';
export const FEEDBACK_OFFSET_TOP = 8;
export const FEEDBACK_HEIGHT_ESTIMATE = 160;

export const INPUT_PART_STORAGE_KEY_PREFIX = 'ai-assistant-active-input-part';

export const PART_TYPES = {
  MESSAGE: 'MESSAGE',
  GUIDED_PROMPT: 'GUIDED_PROMPT',
  CHART: 'CHART',
};

export const QUESTION_TYPES = {
  SINGLE_SELECT: 'SINGLE_SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
};

// TODO: Remove SPOOFED_PROMPT_DATASETS and PROMPT_TRIGGERS once the backend streams parts natively.

export const PROMPT_TRIGGERS = {
  SINGLE_SELECT: 'test:prompt',
  MULTI_SELECT: 'test:prompt:multi',
  REQUIRED: 'test:prompt:required',
  STEPS: 'test:prompt:steps',
  BAR_CHART: 'test:bar-chart',
  BAR_CHART_SINGLE_SERIES: 'test:bar-chart:single-series',
  BAR_CHART_THREE_SERIES: 'test:bar-chart:three-series',
  BAR_CHART_SPARSE: 'test:bar-chart:sparse',
  BAR_CHART_DENSE: 'test:bar-chart:dense',
  BAR_CHART_DESKTOP_DENSE: 'test:bar-chart:desktop-dense',
  BAR_CHART_LONG_LABELS: 'test:bar-chart:long-labels',
  BAR_CHART_MIXED_SIGNS: 'test:bar-chart:mixed-signs',
  BAR_CHART_NEGATIVE: 'test:bar-chart:negative',
  BAR_CHART_INVALID: 'test:bar-chart:invalid',
  BAR_CHART_LARGE_NUMBERS: 'test:bar-chart:large-numbers',
};

export const SPOOFED_PROMPT_DATASETS = {
  [PROMPT_TRIGGERS.SINGLE_SELECT]: [
    {
      text: 'Which cohort would you like to compare against?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: 'Private' },
        { label: 'Public' },
        { label: 'Catholic' },
        { label: 'Regional' },
      ],
      required: false,
    },
  ],
  [PROMPT_TRIGGERS.MULTI_SELECT]: [
    {
      text: 'Which metrics are most important to you?',
      questionType: QUESTION_TYPES.MULTI_SELECT,
      options: [
        { label: 'Enrollment rate' },
        { label: 'Retention rate' },
        { label: 'Graduation rate' },
        { label: 'Yield rate' },
      ],
      required: true,
    },
  ],
  [PROMPT_TRIGGERS.REQUIRED]: [
    {
      text: 'Which academic year should the comparison cover?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: '2023-2024' },
        { label: '2024-2025' },
        { label: '2025-2026' },
      ],
      required: true,
    },
  ],
  [PROMPT_TRIGGERS.STEPS]: [
    {
      text: 'Which cohort would you like to compare against?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: 'Private' },
        { label: 'Public' },
        { label: 'Catholic' },
        { label: 'Regional' },
      ],
      required: true,
    },
    {
      text: 'Which metrics are most important to you?',
      questionType: QUESTION_TYPES.MULTI_SELECT,
      options: [
        { label: 'Enrollment rate' },
        { label: 'Retention rate' },
        { label: 'Graduation rate' },
        { label: 'Yield rate' },
      ],
      required: false,
    },
    {
      text: 'Which academic year should the comparison cover?',
      questionType: QUESTION_TYPES.SINGLE_SELECT,
      options: [
        { label: '2023-2024' },
        { label: '2024-2025' },
        { label: '2025-2026' },
      ],
      required: false,
    },
  ],
};

export const SPOOFED_CHART_DATASETS = {
  [PROMPT_TRIGGERS.BAR_CHART]: {
    text: 'Enrollment growth trend by college',
    chart: {
      chartType: 'BAR',
      title: 'YoY enrollment growth by college',
      subtitle: 'Mocked MARK dataset',
      format: 'PERCENT',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Other',
        ],
      },
      series: [
        { label: 'Fall 2024', values: [3.2, 4.1, -1.5, 5.8, 2.3] },
        { label: 'Fall 2025', values: [4.5, 5.2, 0.8, 6.1, 3.0] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_SINGLE_SERIES]: {
    text: 'Single-series bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Applicant growth by quarter',
      subtitle: 'Single series',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Quarter',
        categories: ['Q1', 'Q2', 'Q3', 'Q4'],
      },
      series: [{ label: '2025', values: [2.1, 3.8, -0.6, 4.3] }],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_THREE_SERIES]: {
    text: 'Three-series bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Applications by college and year',
      subtitle: 'Three series',
      format: 'NUMBER',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Other',
        ],
      },
      series: [
        { label: '2023', values: [1200, 950, 780, 1100, 420] },
        { label: '2024', values: [1330, 1015, 820, 1185, 455] },
        { label: '2025', values: [1410, 1080, 860, 1260, 490] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_SPARSE]: {
    text: 'Sparse-data bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Regional conversion rates',
      subtitle: 'Includes missing data points',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Region',
        categories: [
          'Northeast',
          'Southeast',
          'Midwest',
          'West',
          'International',
        ],
      },
      series: [
        { label: '2024', values: [4.2, 0, 3.6, null, 2.8] },
        { label: '2025', values: [4.9, 3.7, null, 4.1, null] },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_DENSE]: {
    text: 'Mobile-dense bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Monthly inquiries by segment',
      subtitle: '12 bar slots — mobile horizontal default threshold',
      format: 'NUMBER',
      categoryAxis: {
        label: 'Month',
        categories: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
      },
      series: [
        {
          label: '2025',
          values: [320, 345, 360, 380, 410, 430, 450, 470, 495, 520, 545, 570],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_DESKTOP_DENSE]: {
    text: 'Desktop-dense bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Applications by academic program',
      subtitle: '32 bar slots — desktop horizontal default threshold',
      format: 'NUMBER',
      categoryAxis: {
        label: 'Program',
        categories: [
          'Accounting',
          'Biology',
          'Business',
          'Chemistry',
          'Computer Science',
          'Economics',
          'Education',
          'Engineering',
          'English',
          'History',
          'Mathematics',
          'Nursing',
          'Philosophy',
          'Physics',
          'Political Science',
          'Psychology',
        ],
      },
      series: [
        {
          label: 'Fall 2024',
          values: [
            420, 380, 510, 290, 640, 310, 275, 720, 195, 240, 330, 580, 160,
            250, 220, 460,
          ],
        },
        {
          label: 'Fall 2025',
          values: [
            445, 395, 535, 305, 675, 325, 290, 755, 205, 255, 350, 610, 170,
            265, 235, 485,
          ],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_LONG_LABELS]: {
    text: 'Long-label bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Engagement by audience segment',
      subtitle:
        '16 bar slots; includes a 32+ char label for horizontal truncation',
      format: 'NUMBER',
      categoryAxis: {
        label: 'Audience segment',
        categories: [
          'Prospective First-Year Inquiries',
          'Transfer Student Applications',
          'Returning Adult Learner Cohort',
          'Dual-Enrollment High Schoolers',
          'Graduate Program Applicants',
          'Online Degree Seekers',
          'Veteran & Military Affiliated',
          'International Non-Resident First-Year Applicants',
        ],
      },
      series: [
        {
          label: 'Fall 2025',
          values: [39493, 5514, 2816, 1280, 3420, 1875, 940, 2110],
        },
        {
          label: 'Fall 2026',
          values: [41250, 5980, 3010, 1355, 3610, 2040, 1015, 2255],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_MIXED_SIGNS]: {
    text: 'Mixed-sign horizontal bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'YoY enrollment change by college',
      subtitle: 'Dense horizontal layout with positive and negative values',
      format: 'PERCENT',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Education',
          'Nursing',
          'Law',
          'Medicine',
        ],
      },
      series: [
        {
          label: 'Fall 2024',
          values: [4.2, 1.8, 0.6, 3.1, -2.4, 0, -0.9, 2.7],
        },
        {
          label: 'Fall 2025',
          values: [-0.5, 2.3, 3.2, null, 1.1, -1.6, 0, -2.1],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_NEGATIVE]: {
    text: 'Mixed-sign horizontal bar chart (mock)',
    chart: {
      chartType: 'BAR',
      title: 'YoY enrollment change by college',
      subtitle: 'Dense horizontal layout with positive and negative values',
      format: 'PERCENT',
      categoryAxis: {
        label: 'College',
        categories: [
          'Business',
          'Engineering',
          'Liberal Arts',
          'STEM',
          'Education',
          'Nursing',
          'Law',
          'Medicine',
        ],
      },
      series: [
        {
          label: 'Fall 2024',
          values: [-4.2, -1.8, -0.6, -3.1, -2.4, 0, -0.9, -2.7],
        },
        {
          label: 'Fall 2025',
          values: [-0.5, -2.3, -3.2, null, -1.1, -1.6, 0, -2.1],
        },
      ],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_INVALID]: {
    text: 'Invalid bar chart payload (mock)',
    chart: {
      chartType: 'BAR',
      title: 'Invalid chart payload',
      subtitle: 'Series length does not match categories',
      format: 'PERCENT',
      categoryAxis: {
        label: 'Quarter',
        categories: ['Q1', 'Q2', 'Q3'],
      },
      series: [{ label: '2025', values: [12, 20] }],
    },
  },
  [PROMPT_TRIGGERS.BAR_CHART_LARGE_NUMBERS]: {
    text: 'Research funding by college',
    chart: {
      chartType: 'BAR',
      title: 'Research funding by college',
      subtitle: 'Values across thousands, millions, and billions',
      format: 'CURRENCY',
      categoryAxis: {
        label: 'College',
        categories: [
          'Arts',
          'Business',
          'Education',
          'Engineering',
          'Medicine',
          'Nursing',
        ],
      },
      series: [
        {
          label: 'FY 2025',
          values: [845000, 1250000, 9999500, 48760000, 124000000, 34850000],
        },
        {
          label: 'FY 2026',
          values: [912500, 1349999, 10040000, 52150000, 131550000, 35150000],
        },
      ],
    },
  },
};
